import type { ColumnDef } from "@tanstack/react-table";
import { Chip } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { SharedMenuAcciones } from "../../../shared/button";
import type { SharedAccionMenu } from "../../../shared/button";
import type { FilaInventario } from "../types/inventario.types";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { IconLayersSelected } from '@tabler/icons-react';

const COLOR_EDITAR = "#0047AB";
const COLOR_ELIMINAR = "#DC2626";
const COLOR_REACTIVAR = "#15803D";

const ANCHO_CLAVE = 110;
const ANCHO_NOMBRE = 200;
const ANCHO_TIPO_PRODUCTO = 150;
const ANCHO_CATEGORIA = 140;
const ANCHO_UNIDAD_MEDIDA = 120;
const ANCHO_UBICACION = 140;
const ANCHO_ALMACEN = 140;
const ANCHO_PRECIO = 110;
const ANCHO_CANTIDAD = 100;
const ANCHO_MINIMOS = 100;
const ANCHO_MAXIMOS = 100;
const ANCHO_PUNTOREORDEN = 100;
const ANCHO_ESTATUS = 100;
const ANCHO_ACCIONES = 110;

export const ANCHO_MINIMO_INVENTARIO =
    ANCHO_CLAVE +
    ANCHO_NOMBRE +
    ANCHO_TIPO_PRODUCTO +
    ANCHO_CATEGORIA +
    ANCHO_UNIDAD_MEDIDA +
    ANCHO_UBICACION +
    ANCHO_ALMACEN +
    ANCHO_PRECIO +
    ANCHO_CANTIDAD +
    ANCHO_MINIMOS +
    ANCHO_MAXIMOS +
    ANCHO_PUNTOREORDEN +
    ANCHO_ESTATUS +
    ANCHO_ACCIONES;

interface AccionesInventario {
    onVerDetalle?: (fila: FilaInventario) => void;
    onEditar?: (fila: FilaInventario) => void;
    onDesactivar?: (fila: FilaInventario) => void;
    onReactivar?: (fila: FilaInventario) => void;
}

interface TextosInventario {
    clave: string;
    nombre: string;
    tipoProducto: string;
    categoria: string;
    unidadMedida: string;
    ubicacion: string;
    almacen: string;
    precio: string;
    cantidad: string;
    minimos: string;
    maximos: string;
    puntoReorden: string;
    estatus: string;
    verDetalle: string;
    editar: string;
    desactivar: string;
    reactivar: string;
}

export const construirColumnasInventario = (
    acciones: AccionesInventario,
    textos: TextosInventario,
    mostrarEstatus: boolean,
    columnasExtra: ColumnDef<FilaInventario, unknown>[] = []
): ColumnDef<FilaInventario, unknown>[] => {
    const hayAcciones = Boolean(
        acciones.onVerDetalle || acciones.onEditar || acciones.onDesactivar || acciones.onReactivar
    );

    return [
        { accessorKey: "clave", header: textos.clave, meta: { ancho: ANCHO_CLAVE } },
        { accessorKey: "nombre", header: textos.nombre, meta: { ancho: ANCHO_NOMBRE } },
        { accessorKey: "tipoProducto", header: textos.tipoProducto, meta: { ancho: ANCHO_TIPO_PRODUCTO } },
        { accessorKey: "categoria", header: textos.categoria, meta: { ancho: ANCHO_CATEGORIA } },
        { accessorKey: "unidadMedida", header: textos.unidadMedida, meta: { ancho: ANCHO_UNIDAD_MEDIDA } },
        { accessorKey: "ubicacion", header: textos.ubicacion, meta: { ancho: ANCHO_UBICACION } },
        { accessorKey: "almacen", header: textos.almacen, meta: { ancho: ANCHO_ALMACEN } },
        {
            accessorKey: "precio",
            header: textos.precio,
            meta: { ancho: ANCHO_PRECIO, alineacion: "right" },
            cell: ({ row }) =>
                row.original.precio.toLocaleString("es-MX", {
                    style: "currency",
                    currency: "MXN",
                }),
        },
        { accessorKey: "cantidad", header: textos.cantidad, meta: { ancho: ANCHO_CANTIDAD, alineacion: "center" } },
        { accessorKey: "minimos", header: textos.minimos, meta: { ancho: ANCHO_MINIMOS, alineacion: "center" } },
        { accessorKey: "maximos", header: textos.maximos, meta: { ancho: ANCHO_MAXIMOS, alineacion: "center" } },
        { accessorKey: "puntoReorden", header: textos.puntoReorden, meta: { ancho: ANCHO_PUNTOREORDEN, alineacion: "center" } },
        ...columnasExtra,
        ...(mostrarEstatus
            ? ([
                {
                    accessorKey: "estatus",
                    header: textos.estatus,
                    meta: { ancho: ANCHO_ESTATUS, alineacion: "center" },
                    cell: ({ row }) => {
                        const colorEstatus = row.original.activo ? COLOR_REACTIVAR : COLOR_ELIMINAR;
                        return (
                            <Chip
                                label={row.original.estatus}
                                size="small"
                                sx={{
                                    color: colorEstatus,
                                    backgroundColor: `${colorEstatus}1A`,
                                    fontWeight: 700,
                                    border: `1px solid ${colorEstatus}`,
                                }}
                            />
                        );
                    },
                },
            ] as ColumnDef<FilaInventario, unknown>[])
            : []),
        ...(hayAcciones
            ? ([
                {
                    id: "acciones",
                    enableSorting: false,
                    enableColumnFilter: false,
                    header: "Acciones",
                    meta: { ancho: ANCHO_ACCIONES, alineacion: "center" },
                    cell: ({ row }) => {
                        const disponibles: SharedAccionMenu[] = [];

                        if (acciones.onVerDetalle) {
                            disponibles.push({
                                texto: textos.verDetalle,
                                color: "#041d45",
                                icono: <IconLayersSelected stroke={2} />,
                                onClick: () => acciones.onVerDetalle!(row.original),
                            });
                        }

                        if (acciones.onEditar) {
                            disponibles.push({
                                texto: textos.editar,
                                color: COLOR_EDITAR,
                                icono: <EditOutlinedIcon fontSize="inherit" />,
                                onClick: () => acciones.onEditar!(row.original),
                            });
                        }

                        if (mostrarEstatus) {
                            if (row.original.activo && acciones.onDesactivar) {
                                disponibles.push({
                                    texto: textos.desactivar,
                                    color: COLOR_ELIMINAR,
                                    icono: <DeleteOutlineOutlinedIcon fontSize="inherit" />,
                                    onClick: () => acciones.onDesactivar!(row.original),
                                });
                            } else if (!row.original.activo && acciones.onReactivar) {
                                disponibles.push({
                                    texto: textos.reactivar,
                                    color: COLOR_REACTIVAR,
                                    icono: <RestartAltOutlinedIcon fontSize="inherit" />,
                                    onClick: () => acciones.onReactivar!(row.original),
                                });
                            }
                        }

                        return <SharedMenuAcciones acciones={disponibles} />;
                    },
                },
            ] as ColumnDef<FilaInventario, unknown>[])
            : []),
    ];
};